import { FC, useCallback, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { IconProps } from '../../../icons/IconProps'
import { LogoutIcon } from '../../../icons/LogoutIcon'
import { TickmillProductType } from '../../../model/TickmillProductType'
import { useMenu } from '../../context/MenuContext'
import { useProductReadContext } from '../../context/ProductContext'
import { useIBMenu } from './topLevelMenus/useIBMenu'
import { useLanguageMenu } from './topLevelMenus/useLanguageMenu'
import { useLearningMenu } from './topLevelMenus/useLearningMenu'
import { usePAMenu } from './topLevelMenus/usePAMenu'
import { useProfileMenu } from './topLevelMenus/useProfileMenu'
import { useSocialTradingMenu } from './topLevelMenus/useSocialTradingMenu'
import { useStatementsMenu } from './topLevelMenus/useStatementsMenu'
import { useSubscriptionsMenu } from './topLevelMenus/useSubscriptionsMenu'
import { useToolsMenu } from './topLevelMenus/useToolsMenu'
import { useTradersRoomMenu } from './topLevelMenus/useTradersRoomMenu'

interface LuckyDrawState {
  luckyDrawModalActive: boolean
}

export interface MenuSection {
  id: string
  label: string
  path?: string
  icon?: FC<IconProps>
  children?: MenuSection[]
  onClick?: () => void
  alertActive?: boolean
  infoClick?: () => void
  products?: TickmillProductType[]
  condition?: boolean
  isSelected?: boolean
  rightIcon?: FC<IconProps>
  screenChildren?: MenuSection[]
  highlighted?: boolean
  // TODO: re-think this, it seems too specific
  state?: LuckyDrawState
}

export const useAnonymousMenuSections = (): {
  menuSections: MenuSection[]
} => {
  const languageMenu = useLanguageMenu()

  return {
    menuSections: [languageMenu],
  }
}

export const filterMenuSections = (
  sections: MenuSection[],
  product: TickmillProductType
): MenuSection[] =>
  sections
    .filter((section) => section.condition !== false)
    .filter((section) => (section.products ? section.products.includes(product) : true))
    .map((section) =>
      section.children
        ? { ...section, children: filterMenuSections(section.children, product) }
        : section
    )

export const useMenuSections = (): {
  menuSections: MenuSection[]
} => {
  const { t } = useTranslation()
  const { setMenuItems } = useMenu()
  const { product } = useProductReadContext()
  const prevRef = useRef<MenuSection[] | null>(null)

  const tradersRoomMenu = useTradersRoomMenu()
  const IBMenu = useIBMenu()
  const subscriptionsMenu = useSubscriptionsMenu()
  const PAMenu = usePAMenu()
  const profileMenu = useProfileMenu()
  const toolsMenu = useToolsMenu()
  const learningMenu = useLearningMenu()
  const statementsMenu = useStatementsMenu()
  const languageMenu = useLanguageMenu()
  const socialTradingMenu = useSocialTradingMenu()

  const menuSections: MenuSection[] = useMemo(
    () => [
      tradersRoomMenu,
      IBMenu,
      socialTradingMenu,
      PAMenu,
      subscriptionsMenu,
      statementsMenu,
      toolsMenu,
      learningMenu,
      profileMenu,
      languageMenu,
      {
        id: 'logout',
        label: t('Profile.Logout'),
        icon: LogoutIcon,
        path: '/logout',
      },
    ],
    [
      IBMenu,
      PAMenu,
      learningMenu,
      profileMenu,
      statementsMenu,
      subscriptionsMenu,
      t,
      toolsMenu,
      tradersRoomMenu,
      languageMenu,
    ]
  )

  const filteredSections = useCallback(filterMenuSections, [])

  const filteredMenuSections = useMemo(
    () => filteredSections(menuSections, product),
    [filteredSections, menuSections, product]
  )

  useEffect(() => {
    const previous = prevRef.current
    const next = filteredMenuSections

    if (!previous || JSON.stringify(previous) !== JSON.stringify(next)) {
      setMenuItems(next)
      prevRef.current = next
    }
  }, [filteredMenuSections, setMenuItems])

  return { menuSections: filteredMenuSections }
}
